import React, { useMemo } from "react";
import { Flame, FileText, CheckCircle2 } from "lucide-react";

const BAR_COLOR = "#f97316";

/**
 * TopBlockingReasonsChart — horizontal bar chart of the top blocking-reason
 * codes for THIS month, ranked by frequency. Joined against the reason-code
 * catalogue so each bar shows a human description (not just the code).
 */
export default function TopBlockingReasonsChart({ items, catalogue, onGenerateReport }) {
  const data = useMemo(() => {
    const start = new Date();
    start.setDate(1); start.setHours(0, 0, 0, 0);
    const counts = {};
    items.forEach(i => {
      if (!i.is_blocked) return;
      const u = i.updated_at ? new Date(i.updated_at) : null;
      if (!u || u < start) return;
      const code = (i.block_reason_code || "UNCODED").trim() || "UNCODED";
      counts[code] = (counts[code] || 0) + 1;
    });
    const flat = {};
    if (catalogue) {
      Object.entries(catalogue).forEach(([stage, cfg]) => {
        if (cfg.uses_subteam) {
          Object.entries(cfg.subteams || {}).forEach(([sub, codes]) => {
            codes.forEach(c => { flat[c.code] = { ...c, stage, subteam: sub }; });
          });
        } else {
          (cfg.codes || []).forEach(c => { flat[c.code] = { ...c, stage, subteam: "" }; });
        }
      });
    }
    return Object.entries(counts)
      .map(([code, count]) => {
        const meta = flat[code];
        return {
          code,
          count,
          description: meta?.description || (code === "UNCODED" ? "Uncoded / free-text reason" : code),
          stage: meta?.stage || "",
          subteam: meta?.subteam || "",
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [items, catalogue]);

  const totalBlocked = useMemo(() => items.filter(i => i.is_blocked).length, [items]);

  return (
    <div className="rl-glass-edge bg-[hsl(var(--card))] rounded-lg p-4 h-full" data-testid="top-reasons-chart">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-red-500" />
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-[hsl(var(--muted-foreground))]">Top Blocking Reasons · This Month</h3>
          <span className="font-mono-data text-[10px] px-2 py-0.5 rounded-full rl-carved bg-[hsl(var(--muted))]/60 text-[hsl(var(--muted-foreground))]">{totalBlocked} total blocked</span>
        </div>
        <button
          onClick={onGenerateReport}
          className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] px-3 py-1 rounded-full rl-pill border border-orange-500/40 bg-orange-500/5 text-orange-600 dark:text-orange-400 hover:bg-orange-500/10 hover:border-orange-500/60 transition-colors"
          data-testid="generate-attention-report-btn"
        >
          <FileText className="w-3.5 h-3.5" /> Generate Report
        </button>
      </div>

      {data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-[hsl(var(--muted-foreground))]">
          <CheckCircle2 className="w-10 h-10 mb-2 text-emerald-500 opacity-70" />
          <p className="text-sm font-medium">No blocking reasons this month</p>
          <p className="text-xs mt-1 opacity-70">Pipeline is flowing cleanly.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="top-reasons-list">
          {data.map((row, idx) => {
            const maxCount = data[0].count;
            const pct = Math.max(6, (row.count / maxCount) * 100);
            return (
              <div key={row.code} className="flex items-center gap-3">
                <div className="w-[88px] shrink-0">
                  <span className={`font-mono-data text-[11px] font-bold px-2 py-0.5 rounded-full border ${row.code === "UNCODED" ? "border-dashed" : "rl-pill"}`} style={{ color: BAR_COLOR, borderColor: BAR_COLOR + "66", backgroundColor: BAR_COLOR + "0d" }}>{row.code}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-[11px] font-medium text-[hsl(var(--foreground))] truncate" title={row.description}>{row.description}</span>
                    <span className="shrink-0 font-mono-data text-xs font-bold" style={{ color: BAR_COLOR }}>{row.count}<span className="text-[10px] text-[hsl(var(--muted-foreground))] font-normal ml-1">item{row.count !== 1 ? "s" : ""}</span></span>
                  </div>
                  <div className="h-1.5 rounded-full rl-carved bg-[hsl(var(--muted))]/50 overflow-hidden">
                    <div className="h-full rounded-full rl-track-fill" style={{ width: `${pct}%`, background: BAR_COLOR }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
